import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import useSeo, { SITE_NAME, SITE_ORIGIN } from '@/hooks/useSeo';
import SubmitForm from './components/SubmitForm';

const principles = [
  { icon: 'ri-attachment-2', title: '材料优先', desc: '先看材料再下判断。没有可核对形式的材料，我们不发布带有指向性的结论。' },
  { icon: 'ri-user-unfollow-line', title: '化名处理', desc: '在核实完成前统一使用化名与账号代称，尽量降低对无关个人的影响。' },
  { icon: 'ri-equalizer-line', title: '双向呈现', desc: '当事方的回应、反证与澄清同样会出现在页面中，并保留更新记录。' },
  { icon: 'ri-eraser-line', title: '可纠可撤', desc: '收到有效异议后暂停推荐，核实后更新标注或撤稿，处理结果公开说明。' },
];

const steps = [
  { no: '01', title: '材料初审', desc: '确认材料完整度与来源，剔除无法追溯的转述。' },
  { no: '02', title: '交叉比对', desc: '比对录音时间点、转账时间与合同条款，寻找印证点。' },
  { no: '03', title: '当事方回应', desc: '给予当事方不少于 48 小时的回应时间。' },
  { no: '04', title: '标注发布', desc: '标注核实状态，并保留后续更新与修订记录。' },
];

const voices = [
  { name: '合作机构 王先生', text: '被爆料之后最怕没有申诉渠道。这里给了明确的邮箱和回应期限，流程是清楚的。' },
  { name: '读者 陈女士', text: '它把录音的时间点和合同条款对在一起看，比单看一条爆料帖子更接近事情本身。' },
  { name: '投稿人 小林', text: '对接的人会先问材料范围，而不是催我赶紧发。这一点挺难得。' },
];

export default function About() {
  useSeo({
    title: `关于我们 - 内容原则与投稿方式 | ${SITE_NAME}`,
    description:
      '了解网红爆料网的定位、内容原则与核实流程。我们坚持实锤为本，只展示可核对的录音、视频证据、合同照片与转账记录，并提供投稿与异议处理渠道。',
    keywords: '网红爆料网介绍,爆料核实流程,内容原则,爆料投稿,异议处理',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: `关于 ${SITE_NAME}`,
        url: `${SITE_ORIGIN}/about`,
        inLanguage: 'zh-CN',
        description: '介绍网站定位、内容原则与材料核实流程。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        url: `${SITE_ORIGIN}/about`,
        name: '联系与投稿',
        inLanguage: 'zh-CN',
        description: '投稿、异议与商务联系渠道。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: SITE_ORIGIN },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${SITE_ORIGIN}/about` },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">关于网红爆料网</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            我们是一个以材料为核心的内容整理平台，围绕网络红人相关的合约、分成、带货与售后争议，集中收录录音、视频证据、
            合同照片与转账记录，并按统一规范标注核实状态。
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-10 px-4 md:px-6 lg:grid-cols-2">
          <div className="h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20investigative%20editorial%20workspace%20with%20a%20laptop%20showing%20generic%20document%20review%2C%20printed%20papers%2C%20magnifier%20and%20headphones%20on%20a%20wooden%20desk%2C%20soft%20daylight%2C%20muted%20amber%20and%20stone%20tones%2C%20clean%20minimal%20background&width=900&height=600&seq=baoliao-about-desk&orientation=landscape"
              alt="网红爆料网的内容整理与材料核对工作场景示意"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div>
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">Our Position</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们为什么只做材料整理</h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              <p>
                网络上的爆料常常只有情绪和结论，缺少可以核对的细节。我们希望把注意力放回材料本身：一段录音在什么时间点说了什么，
                一笔转账与合同条款能否对得上，一份协议的照片是否完整。
              </p>
              <p>
                因此站点不做"谁对谁错"的判决，只呈现投稿人提供的材料与我们的核实进度。读者可以据此自行判断，
                当事方也随时可以提交回应与反证。
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/cases"
                className="whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                浏览已公开内容
              </Link>
              <a
                href="#submit"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                跳转到投稿表单
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            下面四条原则贯穿于每一条内容的整理与发布过程，也是我们接受投稿与合作时的基本前提。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{p.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">核实流程</h2>
          <ol className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step) => (
              <li key={step.no} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="font-heading text-3xl font-black text-accent-500">{step.no}</span>
                <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">来自合作方与读者的反馈</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {voices.map((v) => (
              <figure key={v.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-chat-quote-line text-2xl text-accent-500"></i>
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">{v.text}</blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-3 text-sm font-medium text-foreground-900">
                  {v.name}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系方式</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className="ri-mail-send-line text-xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">投稿与材料对接</h3>
              <a href="mailto:contact@baoliao.example.com" className="mt-1.5 block text-sm text-primary-600 hover:underline">
                contact@baoliao.example.com
              </a>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className="ri-shield-star-line text-xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">异议与撤稿</h3>
              <a href="mailto:appeal@baoliao.example.com" className="mt-1.5 block text-sm text-primary-600 hover:underline">
                appeal@baoliao.example.com
              </a>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className="ri-briefcase-line text-xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">商务与内容合作</h3>
              <a href="mailto:partner@baoliao.example.com" className="mt-1.5 block text-sm text-primary-600 hover:underline">
                partner@baoliao.example.com
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="submit" className="scroll-mt-24 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="text-xs font-medium uppercase tracking-widest text-primary-600">Submit</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">提交你的材料</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                填写下面的表单，简单说明事情经过与手上的材料类型。收到后我们会先与你核对材料范围，
                确认展示方式后再决定是否公开，全程不会收取任何费用。
              </p>
              <ul className="mt-5 space-y-3 text-sm text-foreground-700">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  2 个工作日内回复，先沟通再整理
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  可指定需要脱敏的信息范围
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
                  如未达成一致，材料不会被公开
                </li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              <SubmitForm />
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}